import useUser from "@/hooks/useUser"
import { apiClientV2 } from "@/services/api/apiClient"
import { useMutation, useQuery } from "@tanstack/react-query"
import type { HelpContent } from "./types"

/**
 * The whole visible help tree in one request, articles included.
 *
 * Fetched once and kept: the corpus is small, global, and only changes when
 * someone publishes, so every screen in the helpdesk — overview, category,
 * article, search — reads the same cached payload instead of asking again.
 *
 * Keyed by organisation, not only by language. The response is filtered by the
 * caller's feature flags, and the QueryClient is module-scoped — it outlives
 * sign-out, which does not clear it. Without the organisation in the key, the
 * next session in the same tab could be served the previous one's feature-gated
 * articles straight from cache, with no request and so no server-side check.
 * The query stays disabled until the organisation is known, so nothing is ever
 * cached under an unidentified caller.
 */
export function useHelpContent(lang: string) {
  const { user } = useUser()
  const organization = user?.organization

  const query = useQuery({
    queryKey: ["helpdesk", "content", organization, lang],
    queryFn: async (): Promise<HelpContent> => {
      const response = await apiClientV2.get("/help/content", {
        params: { lang },
      })
      return response.data as HelpContent
    },
    enabled: Boolean(organization),
    staleTime: 15 * 60 * 1000,
  })

  return {
    ...query,
    /** True while the query runs *or* while we are still resolving who is asking. */
    isLoading: query.isLoading || !organization,
  }
}

/**
 * "Was this helpful?".
 *
 * Stored as well as tracked: the vote is only useful if whoever maintains the
 * page can see it, and the editing tree shows the counts next to the page they
 * belong to. One answer per reader per page, so a second click replaces the
 * first rather than counting twice.
 */
export function useSubmitArticleFeedback(articleId: string, lang: string) {
  return useMutation({
    mutationFn: async (helpful: boolean): Promise<void> => {
      await apiClientV2.post(
        `/help/articles/${articleId}/feedback`,
        { helpful },
        { params: { lang } },
      )
    },
  })
}
